import React, { useEffect, useRef, useState } from "react";
import { Send, Sparkles, Loader2 } from "lucide-react";
import { sendChat } from "../../lib/site";
import { toast } from "sonner";

function makeSessionId() {
  return "fs-" + Math.random().toString(36).slice(2) + "-" + Date.now().toString(36);
}

const QUICK_CHIPS = [
  "How much does this cost?",
  "How long is setup?",
  "Will this work for my business?",
  "Can it book appointments?",
];

const INITIAL_BOT = {
  role: "assistant",
  content:
    "Hi — I'm the Always On Sales agent, built by Favor Studio. I can answer your questions on pricing, setup, and exactly how this works for your business. Ask me anything.",
};

export default function ChatWidget() {
  const [sessionId] = useState(() => makeSessionId());
  const [messages, setMessages] = useState([INITIAL_BOT]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const send = async (text) => {
    const trimmed = (text || "").trim();
    if (!trimmed || loading) return;
    setMessages((m) => [...m, { role: "user", content: trimmed }]);
    setInput("");
    setLoading(true);
    try {
      const res = await sendChat(sessionId, trimmed);
      setMessages((m) => [...m, { role: "assistant", content: res.reply }]);
    } catch (e) {
      console.error(e);
      toast.error("Agent is briefly unavailable. Try again in a moment.");
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            "Apologies — I had a brief connection hiccup. Try asking again, or book a free strategy call: https://calendly.com/favorstudio/new-meeting",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="chat-widget"
      className="bg-white border border-[#0A0F1E]/10 rounded-2xl shadow-[0_30px_80px_-30px_rgba(10,15,30,0.20)] overflow-hidden"
    >
      {/* Header */}
      <div className="bg-[#0A0F1E] text-white px-5 py-4 flex items-center gap-3">
        <div className="h-9 w-9 rounded-full bg-[#C9A84C] flex items-center justify-center text-[#0A0F1E]">
          <Sparkles size={17} strokeWidth={2} />
        </div>
        <div className="flex-1">
          <div className="text-[0.7rem] uppercase tracking-[0.22em] text-[#C9A84C]">Always On Sales</div>
          <div className="text-sm font-medium text-white/90">Live Demo · Online now</div>
        </div>
        <span className="flex items-center gap-1.5 text-[0.7rem] text-white/70">
          <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
          Live
        </span>
      </div>

      {/* Conversation */}
      <div
        ref={scrollRef}
        className="fs-chat-scroll px-5 py-5 h-[360px] overflow-y-auto bg-[#FAFAF8] space-y-3"
        data-testid="chat-messages"
      >
        {messages.map((m, i) => (
          <Bubble key={i} role={m.role} content={m.content} />
        ))}
        {loading && (
          <div className="flex items-center gap-2 text-xs text-[#0F172A]/55">
            <Loader2 size={14} className="animate-spin" />
            Agent is typing…
          </div>
        )}
      </div>

      {/* Quick chips */}
      <div className="px-5 pt-3 pb-2 flex flex-wrap gap-2 border-t border-[#0A0F1E]/10 bg-white">
        {QUICK_CHIPS.map((q, i) => (
          <button
            key={i}
            data-testid={`chat-chip-${i}`}
            onClick={() => send(q)}
            disabled={loading}
            className="text-xs px-3 py-1.5 rounded-full border border-[#0A0F1E]/15 text-[#0A0F1E]/80 hover:border-[#C9A84C] hover:text-[#0A0F1E] transition-colors disabled:opacity-50"
          >
            {q}
          </button>
        ))}
      </div>

      {/* Input */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          send(input);
        }}
        className="px-5 pb-5 pt-2 bg-white"
      >
        <div className="flex items-center gap-2 border border-[#0A0F1E]/15 rounded-lg px-3 py-2 focus-within:border-[#C9A84C] transition-colors">
          <input
            data-testid="chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask anything..."
            className="flex-1 outline-none text-sm bg-transparent text-[#0A0F1E] placeholder:text-[#0F172A]/40"
            disabled={loading}
          />
          <button
            type="submit"
            data-testid="chat-send"
            disabled={loading || !input.trim()}
            className="h-8 w-8 rounded-md bg-[#0A0F1E] text-white flex items-center justify-center hover:bg-[#0F172A] transition-colors disabled:opacity-40"
            aria-label="Send"
          >
            <Send size={15} strokeWidth={2} />
          </button>
        </div>
        <p className="mt-2 text-[0.7rem] text-[#0F172A]/45 tracking-wide">
          Powered by Always On Sales · This is the real agent your business gets.
        </p>
      </form>
    </div>
  );
}

function Bubble({ role, content }) {
  const isUser = role === "user";
  // Render links inside the content as anchors
  const parts = content.split(/(https?:\/\/\S+)/g);
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[85%] text-sm leading-relaxed px-4 py-2.5 rounded-2xl whitespace-pre-wrap ${
          isUser
            ? "bg-[#0A0F1E] text-white rounded-br-sm"
            : "bg-white border border-[#0A0F1E]/10 text-[#0A0F1E] rounded-bl-sm"
        }`}
      >
        {parts.map((p, i) =>
          /^https?:\/\//.test(p) ? (
            <a
              key={i}
              href={p}
              target="_blank"
              rel="noopener noreferrer"
              className={`underline ${isUser ? "text-[#C9A84C]" : "text-[#C9A84C]"}`}
            >
              {p}
            </a>
          ) : (
            <span key={i}>{p}</span>
          )
        )}
      </div>
    </div>
  );
}
